import React, { useMemo, useState, Fragment, } from 'react';
import { useList, useToggle, useCounter, useAsyncRetry, } from 'react-use';
import { fromPairs, keyBy, sumBy, sum, isEmpty, chunk, orderBy, groupBy, pickBy, uniq, mapValues } from 'lodash';
import { startOfMonth, endOfMonth, startOfDay, endOfDay, addDays, format as formatDate, differenceInCalendarDays, } from 'date-fns';
import { doc, collection, query, where, orderBy as dbOrderBy, limit, getCountFromServer, getAggregateFromServer, sum as dbSum, count as dbCount, setDoc, } from 'firebase/firestore';
import numeral from 'numeral';
import classnames from 'classnames';

import firebase, { functions, batch, getCollectionData, } from '../../firebase';
import { fields as productFields, } from '../../shared/models/product';
import { taxRates } from '../../shared/config';
import { excludeTax, includeTax } from '../../shared/util';
import { resolveUnitCostAt, normalizeEffectiveDate, netQuantityOf, shippingQuantityOf } from '../../shared/unitCostHistory';
import { httpsCallable } from 'firebase/functions';
import useQueryParams from '../hooks/useQueryParams';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useTenant from '../hooks/useTenant';
import TenantPage from '../TenantPage';
import ModelFormModal from '../modals/ModelFormModal';
import ProductFormModal from '../modals/ProductFormModal';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ProgressButton from '../ProgressButton';
import QuerySelector from '../QuerySelector';
import QueryDateRangeSelector from '../QueryDateRangeSelector';
import QueryInput from '../QueryInput';
import QueryBoolean from '../QueryBoolean';
import OverlayLoading from '../OverlayLoading';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Button, UncontrolledPopover, PopoverBody } from 'reactstrap';
import qs from 'qs';
import ImportMappingButton from '../ImportMappingButton';
import ProductKpiExportButton from '../ProductKpiExportButton';
import NumberDiscrepancyHelpButton from '../NumberDiscrepancyHelpButton';
import ExportButton from '../ExportButton';
import ModalButton from '../ModalButton';
import DailyProductKpiModal from '../modals/DailyProductKpiModal';
import MallBadge from '../MallBadge';
import { columnDefinitions, renderKpiCells, defaultVisibleColumns } from '../productKpiColumns';
import { getMallDefinition, getParentKey } from '../../shared/models/mallDefinitions';
import { canUseShopify as canUseShopifyFor } from '../../shared/models/shop';

const { keys, entries } = Object;
const getProductKpis = httpsCallable(functions, 'getProductKpis');

const leftColumnWidths = [40, 120, 120, 100, 200];

// CSSセレクタとして安全なidに変換する（product.idにスペース等が含まれてもPopover/TooltipのquerySelectorで壊れないように）
const toSafeDomId = _ => String(_).replace(/[^a-zA-Z0-9_-]/g, '_');


function getPageItems(currentPage, totalPages, siblingCount = 1) {
  const totalNumbers = siblingCount * 2 + 5;
  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const left = Math.max(currentPage - siblingCount, 2);
  const right = Math.min(currentPage + siblingCount, totalPages - 1);
  const showLeftDots = left > 2;
  const showRightDots = right < totalPages - 1;
  const middle = Array.from({ length: right - left + 1 }, (_, i) => left + i);
  return [
    1,
    ...(showLeftDots ? ['left-dots'] : []),
    ...middle,
    ...(showRightDots ? ['right-dots'] : []),
    totalPages,
  ];
}

function Pagination({ page, totalPages, hasPrevPage, hasNextPage, onChange }) {
  const [jumpValue, setJumpValue] = useState('');
  const items = getPageItems(page, totalPages, 3);
  const handleJump = () => {
    const n = parseInt(jumpValue, 10);
    if (!isNaN(n) && n >= 1 && n <= totalPages && n !== page) onChange(n);
    setJumpValue('');
  };
  return (
    <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 mt-3">
      <nav>
        <ul className="pagination flex-wrap justify-content-center gap-1 mb-0">
          <li className={`page-item ${!hasPrevPage ? 'disabled' : ''}`}>
            <Button className="page-link" color="secondary" outline onClick={() => onChange(page - 1)} disabled={!hasPrevPage}>前へ</Button>
          </li>
          {items.map((item, i) => (
            typeof item === 'string' ? (
              <li key={item} className="page-item disabled">
                <span className="page-link">…</span>
              </li>
            ) : (
              <li key={item} className={`page-item ${item === page ? 'active' : ''}`}>
                <Button className="page-link" color="secondary" outline onClick={() => onChange(item)}>{item}</Button>
              </li>
            )
          ))}
          <li className={`page-item ${!hasNextPage ? 'disabled' : ''}`}>
            <Button className="page-link" color="secondary" outline onClick={() => onChange(page + 1)} disabled={!hasNextPage}>次へ</Button>
          </li>
        </ul>
      </nav>
      <div className="d-flex align-items-center gap-1 text-muted small">
        <span>{page} / {totalPages}</span>
        <input
          type="number"
          className="form-control form-control-sm"
          style={{ width: 70 }}
          min={1}
          max={totalPages}
          value={jumpValue}
          placeholder="ページ"
          onChange={e => setJumpValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleJump()}
        />
        <Button size="sm" color="secondary" outline onClick={handleJump} disabled={!jumpValue}>移動</Button>
      </div>
    </div>
  );
}

function ColumnHelp({ columnKey, help }) {
  const id = `column-help-${columnKey}`;
  return (
    <Fragment>
      <span
        id={id}
        className="text-muted ms-1"
        style={{ cursor: 'help', fontWeight: 'normal' }}
        onClick={e => e.stopPropagation()}
      >
        ⓘ
      </span>
      <UncontrolledPopover target={id} trigger="legacy" placement="top">
        <PopoverBody style={{ wordBreak: 'break-word', maxWidth: 320, fontWeight: 'normal' }}>{help}</PopoverBody>
      </UncontrolledPopover>
    </Fragment>
  );
}

function SortableTh({ field, currentSortBy, currentSortOrder, onSort, children, style }) {
  const handleClick = () => onSort(field);
  const isActive = currentSortBy === field;
  const sortIcon = isActive ? (currentSortOrder === 'desc' ? '↓' : '↑') : '';
  
  return (
    <th style={{ cursor: 'pointer', ...style }} onClick={handleClick} className="text-primary">
      {children} {sortIcon}
    </th>
  );
}


export default function ProductKpiAnalysis (props) {
  return (
    <TenantPage>
      <PageContent {...props} />
    </TenantPage>
  );
};

// 表示列設定のフィールド定義
const visibleColumnsFields = fromPairs(
  columnDefinitions.map(_ => [_.key, { label: _.label, type: 'boolean', initialValue: true }])
);

function PageContent (props) {
  const { tenant, user } = props;
  const queryParams = useQueryParams();
  // テナント設定「税抜き表示のデフォルト」をデフォルトに。URLクエリ指定があればそちら優先
  const defaultExcludeTax = tenant.exportExcludeTax === 'excluded';
  const isExcludeTax = queryParams.excludeTax != null ? queryParams.excludeTax === '1' : defaultExcludeTax;
  const defaultStartDate = addDays(new Date(), -29);
  const defaultEndDate = new Date();
  const dateRange = useMemo(_ => queryParams.dateRange || [defaultStartDate, defaultEndDate], [queryParams.dateRange?.[0], queryParams.dateRange?.[1]]);
  const startDate = useMemo(_ => dateRange[0] ? new Date(dateRange[0]) : defaultStartDate, [dateRange]);
  const endDate = useMemo(_ => dateRange[1] ? new Date(dateRange[1]) : (dateRange[0] ? endOfMonth(new Date(dateRange[0])) : defaultEndDate), [dateRange]);
  const sellers = useCollectionSubscription(query(collection(tenant.ref, 'sellers')));
  const sellersById = keyBy(sellers, 'id');
  const productTags = useCollectionSubscription(query(collection(tenant.ref, 'productTags')));
  const productTagsById = keyBy(productTags, 'id');
  const shops = useCollectionSubscription(query(collection(tenant.ref, 'shops')));
  const shopsById = keyBy(shops, 'id');
  const { items: syncAmazonProductsJobs, isLoading: isLoadingSyncAmazonProductsJobs } = useCollectionSubscription(query(collection(tenant.ref, 'syncAmazonProductsJobs')), [], { detail: true });
  const { items: syncRakutenProductsJobs, isLoading: isLoadingSyncRakutenProductsJobs } = useCollectionSubscription(query(collection(tenant.ref, 'syncRakutenProductsJobs')), [], { detail: true });
  const { items: syncShopifyProductsJobs, isLoading: isLoadingSyncShopifyProductsJobs } = useCollectionSubscription(query(collection(tenant.ref, 'syncShopifyProductsJobs')), [], { detail: true });

  // 表示列設定を取得（ユーザーごと）
  const productKpiSettingsRef = doc(user.ref, 'settings', 'productKpiAnalysis');
  const productKpiSettings = useDocumentSubscription(productKpiSettingsRef);
  const visibleColumns = useMemo(_ => {
    if (!productKpiSettings) return defaultVisibleColumns;
    return columnDefinitions.filter(col => productKpiSettings[col.key] !== false).map(_ => _.key);
  }, [productKpiSettings]);
  const isSyncingAmazon = syncAmazonProductsJobs.some(_ => _.status === 'initial');
  const isSyncingRakuten = syncRakutenProductsJobs.some(_ => _.status === 'initial');
  const isSyncingShopify = syncShopifyProductsJobs.some(_ => _.status === 'initial');
  const onClickSyncAmazon = async () => {
    if(!window.confirm('Amazonから商品を取得します。よろしいですか？')) return;

    await batch(sellers, (batch, seller) => {
      batch.set(doc(collection(tenant.ref, 'syncAmazonProductsJobs')), {
        status: 'initial',
        version: 2,
        sellerId: seller.id,
        createdAt: new Date(),
      });
    });
  };
  const onClickSyncRakuten = async () => {
    if(!window.confirm('楽天から商品を取得します。よろしいですか？')) return;

    await batch(shops.filter(_ => _.mall === 'rakuten'), (batch, shop) => {
      batch.set(doc(collection(tenant.ref, 'syncRakutenProductsJobs')), {
        status: 'initial',
        shopId: shop.id,
        createdAt: new Date(),
      });
    });
  };

  const onClickSyncShopify = async () => {
    if(!window.confirm('Shopifyから商品を取得します。よろしいですか？')) return;

    await batch(shops.filter(_ => _.mall === 'shopify' && _.shopifyAuthorizedAt), (batch, shop) => {
      batch.set(doc(collection(tenant.ref, 'syncShopifyProductsJobs')), {
        status: 'initial',
        shopId: shop.id,
        createdAt: new Date(),
      });
    });
  };

  const sortBy = queryParams.sortBy || 'sales';
  const sortOrder = queryParams.sortOrder || 'desc';

  // Import fields for unit cost
  const importFields = {
    sku: {
      label: 'SKU',
      type: 'string',
      validations: { required: true },
    },
    effectiveDate: {
      label: '原価適用開始日',
      type: 'string',
      validations: {
        // 空欄可。Excel が yyyy-MM-dd を yyyy/M/d に書き換えるため、スラッシュ区切りも受け付けて保存時に正規化する
        dateFormat: v => isEmpty(v) || /^\d{4}[/-]\d{1,2}[/-]\d{1,2}$/.test(v.trim()),
      },
    },
    unitCost: {
      label: '原価単価(税込)',
      type: 'integer',
      validations: { required: true },
    },
    unitShippingCost: {
      label: '配送料単価(税込)',
      type: 'integer',
      validations: {},
    },
    costTaxRate: {
      label: '原価税率',
      type: 'string',
      validations: {},
    },
  };

  const taxRateKeyByLabel = fromPairs(entries(taxRates).map(([k, v]) => [v.label, k]));

  // 数値CSV項目の空判定（lodash isEmpty は 0 を空扱いするため使わない）
  const isBlank = v => v == null || (typeof v === 'string' && v.trim() === '');

  const handleImport = async (rows) => {
    const skus = uniq(rows.map(_ => _.sku));
    const skuChunks = chunk(skus, 10);

    const productsArrays = await Promise.all(
      skuChunks.map(_ => getCollectionData(query(collection(tenant.ref, 'products'), where('sku', 'in', _))))
    );
    const products = productsArrays.flat();
    // 同じSKUがAmazon/楽天など複数モールに存在する場合は同一商品とみなし、全件に同じ原価を反映する。
    const productsGroupedBySku = groupBy(products, 'sku');

    // 発効日つき行は原価改定履歴(unitCostHistory)として扱う。
    // 同一SKUの発効日つき全行で履歴を丸ごと洗い替え（CSVが正）。
    const historyRowsGroupedBySku = groupBy(rows.filter(_ => !isEmpty(_.effectiveDate)), 'sku');
    // 注意: このコンポーネントには queryParams.sortBy 由来のローカル変数 `sortBy`(文字列)があり、
    // lodash の sortBy を同スコープで覆い隠すため、ここでは同義の orderBy を使う。
    const unitCostHistoryBySku = mapValues(historyRowsGroupedBySku, historyRows =>
      orderBy(
        historyRows.map(_ => ({ effectiveDate: normalizeEffectiveDate(_.effectiveDate), unitCost: parseInt(_.unitCost, 10) })),
        'effectiveDate'
      )
    );

    // SKUごとに商品ドキュメントへの更新データを組み立てる。
    // 発効日なし行 → 原始原価(unitCost)等を更新。発効日あり行 → unitCostHistory を洗い替え。
    const updateDataBySku = mapValues(groupBy(rows, 'sku'), (skuRows, sku) => {
      const baseRow = skuRows.find(_ => isEmpty(_.effectiveDate));
      const costTaxRateKey = baseRow && taxRateKeyByLabel[baseRow.costTaxRate?.trim()];
      return {
        // isEmpty は 0 も空扱いするため、数値項目は「空文字/null/undefined でなければ値あり」で判定する
        ...(baseRow && !isBlank(baseRow.unitCost) ? { unitCost: parseInt(baseRow.unitCost, 10) } : {}),
        ...(baseRow && !isBlank(baseRow.unitShippingCost) ? { unitShippingCost: parseInt(baseRow.unitShippingCost, 10) } : {}),
        ...(costTaxRateKey ? { costTaxRate: costTaxRateKey } : {}),
        ...(unitCostHistoryBySku[sku] ? { unitCostHistory: unitCostHistoryBySku[sku] } : {}),
      };
    });

    // 1SKUが複数商品にヒットしうるので (product, updateData) のペアに展開してから書き込む。
    const updates = Object.entries(updateDataBySku).flatMap(([sku, updateData]) => {
      const matchedProducts = productsGroupedBySku[sku] || [];
      return isEmpty(updateData) ? [] : matchedProducts.map(product => ({ product, updateData }));
    });

    await batch(updates, (batchWrite, { product, updateData }) => batchWrite.update(product.ref, updateData));
  };

  // 原価設定エクスポート（現在の原価＋改定履歴を出力し、そのままインポートで往復できる）。
  // 列はインポート項目(importFields のラベル)に揃える。履歴を持つ商品は適用開始日つきで複数行、
  // 持たない商品は適用開始日を空欄にした1行を出力する。
  const unitCostSettingsExportRows = async () => {
    const productChunks = await getCollectionData(collection(tenant.ref, 'productChunks'));
    const allProducts = productChunks.flatMap(_ => entries(_.data || {}).map(([id, product]) => ({ ...product, id })));
    // 子あり親商品は原価を持たないので除外（子・単品のみ出力）
    const costProducts = allProducts.filter(_ =>
      !(_.amazonAttributes?.isParent) && !(_.rakutenAttributes?.isParent) && !(_.shopifyAttributes?.isParent) && !(_.yahooAttributes?.isParent)
    );
    const toRow = (product, { effectiveDate = '', unitCost, includeBase = false }) => ({
      sku: product.sku || '',
      productName: product.name || '',
      effectiveDate,
      unitCost: unitCost ?? '',
      // 配送料・税率は履歴化しないため、原始行(includeBase)にのみ出す
      unitShippingCost: includeBase ? (product.unitShippingCost ?? '') : '',
      costTaxRate: includeBase ? (taxRates[product.costTaxRate]?.label ?? '') : '',
    });
    return orderBy(costProducts, 'sku').flatMap(product => {
      const baseRow = toRow(product, { unitCost: product.unitCost ?? '', includeBase: true });
      const historyRows = orderBy(product.unitCostHistory || [], 'effectiveDate')
        .map(_ => toRow(product, { effectiveDate: _.effectiveDate, unitCost: _.unitCost }));
      return [baseRow, ...historyRows];
    }).map(mapExportKeysToLabel);
  };

  // エクスポートCSVの見出しはインポート項目(importFields のラベル)に揃える。商品名は参考列。
  const exportKeyToLabel = {
    ...fromPairs(entries(importFields).map(([k, v]) => [k, v.label])),
    productName: '商品名',
    memo: 'メモ',
  };
  const mapExportKeysToLabel = row => fromPairs(entries(row).map(([k, v]) => [exportKeyToLabel[k] ?? k, v]));
  const mapSampleKeysToLabel = mapExportKeysToLabel;
  const sampleRowsForImport = async () => [
    {
      sku: 'SAMPLE-SKU-001',
      productName: 'サンプル商品1',
      effectiveDate: '',
      unitCost: 1000,
      unitShippingCost: 500,
      costTaxRate: '10%',
      memo: '※適用開始日が空欄なら従来通り原価を更新します',
    },
    {
      sku: 'SAMPLE-SKU-002',
      productName: 'サンプル商品2',
      effectiveDate: '2025-05-01',
      unitCost: 2000,
      unitShippingCost: '',
      costTaxRate: '',
      memo: '※適用開始日を入れると原価改定履歴になります(yyyy-MM-dd)',
    },
    {
      sku: 'SAMPLE-SKU-002',
      productName: 'サンプル商品2',
      effectiveDate: '2025-07-01',
      unitCost: 2200,
      unitShippingCost: '',
      costTaxRate: '',
      memo: '※同じSKUの適用開始日つき行が履歴として全て反映されます',
    },
    {
      sku: 'SAMPLE-SKU-002',
      productName: 'サンプル商品2',
      effectiveDate: '',
      unitCost: 2000,
      unitShippingCost: 600,
      costTaxRate: '8%',
      memo: '※【注意】適用開始日つき行はCSVの内容で履歴を丸ごと置き換えます(洗い替え)',
    },
  ].map(mapSampleKeysToLabel);

  return (
    <div className="product-kpi-analysis container-fluid">
      <div className="px-4" style={{ maxWidth: 'calc(100vw - var(--side-nav-width))' }}>
        <div className="card p-4">
          <div className="mb-1 d-flex flex-wrap gap-1 align-items-end justify-content-start">
            <ProgressButton color="secondary" process={onClickSyncAmazon} disabled={isSyncingAmazon || isLoadingSyncAmazonProductsJobs}>
              {isSyncingAmazon && <span className="fas fa-spin fa-spinner me-1" />}
              Amazonから商品を取得
            </ProgressButton>
            {shops.some(_ => _.mall === 'rakuten') && (
              <ProgressButton color="secondary" process={onClickSyncRakuten} disabled={isSyncingRakuten || isLoadingSyncRakutenProductsJobs}>
                {isSyncingRakuten && <span className="fas fa-spin fa-spinner me-1" />}
                楽天から商品を取得
              </ProgressButton>
            )}
            {canUseShopifyFor(user) && shops.some(_ => _.mall === 'shopify' && _.shopifyAuthorizedAt) && (
              <ProgressButton color="secondary" process={onClickSyncShopify} disabled={isSyncingShopify || isLoadingSyncShopifyProductsJobs}>
                {isSyncingShopify && <span className="fas fa-spin fa-spinner me-1" />}
                Shopifyから商品を取得
              </ProgressButton>
            )}
          </div>
          <div className="mb-3 d-flex flex-wrap gap-2 align-items-end justify-content-between">
            <div className="d-flex flex-wrap gap-1 align-items-end justify-content-start">
              <QueryDateRangeSelector
                paramName="dateRange"
                label="期間"
                defaultValue={[startDate, endDate]}
              />
              <QuerySelector
                paramName="ownerIds"
                label="ショップ"
                isMulti
                width={200}
                options={orderBy([
                  ...sellers.map(_ => ({ value: `seller:${_.id}`, label: _.name || _.sellerId, index: _.index })),
                  ...shops.map(_ => ({ value: `shop:${_.id}`, label: _.name, index: _.index })),
                ], _ => _.index ?? Infinity)}
              />
              <QueryInput label="ASIN / SKU / 商品名" paramName="keyword" />
              <QueryBoolean paramName="onlyNoPurchases" label="原価未入力" />
              <QueryBoolean paramName="excludeTax" label="税抜き" defaultValue={defaultExcludeTax ? '1' : '0'} />
              <QuerySelector
                paramName="tagIds"
                label="タグ"
                isMulti
                width={200}
                options={productTags.map(_ => ({ value: _.id, label: _.name }))}
              />
              <EditButton
                label="表示列"
                icon={<span className="fas fa-columns" />}
                type="set"
                itemRef={productKpiSettingsRef}
                FormModal={ModelFormModal}
                formProps={{ title: '表示列設定', fields: visibleColumnsFields }}
              />
            </div>
            <div className="d-flex flex-wrap gap-1 text-nowrap align-items-end justify-content-start">
              <NumberDiscrepancyHelpButton sellers={sellers} shops={shops} forProductKpi />
              <ProductKpiExportButton
                tenant={tenant}
                startDate={startDate}
                endDate={endDate}
                sortBy={sortBy}
                sortOrder={sortOrder}
                keyword={queryParams.keyword}
                onlyNoPurchases={queryParams.onlyNoPurchases === '1'}
                tagIds={!isEmpty(queryParams.tagIds) ? (Array.isArray(queryParams.tagIds) ? queryParams.tagIds : [queryParams.tagIds]) : undefined}
                sellersById={sellersById}
                isExcludeTax={isExcludeTax}
              />
              <ModalButton
                label="原価設定"
                title="原価設定（CSVインポート / エクスポート）"
                modalProps={{ style: { minWidth: 560 } }}
                content={_ => (
                  <div className="d-flex flex-column gap-3">
                    <div>
                      <div className="fw-bold mb-1">エクスポート</div>
                      <div className="small text-muted mb-2">
                        現在の原価と改定履歴をCSVに出力します。編集してそのままインポートで戻せます。
                      </div>
                      <ExportButton
                        label="原価設定をエクスポート"
                        fileName={`原価設定_${formatDate(new Date(), 'yyyy-MM-dd')}.csv`}
                        rows={unitCostSettingsExportRows}
                      />
                    </div>
                    <div className="border-top pt-3">
                      <div className="fw-bold mb-1">インポート</div>
                      <div className="small text-muted mb-2">
                        「原価適用開始日」が空欄の行は原価を更新します。<br />
                        日付(yyyy-MM-dd)を入れた行は原価改定履歴になります。
                        <span className="text-danger">同じSKUの日付つき行はCSVの内容で履歴を丸ごと置き換えます（洗い替え）。</span>
                      </div>
                      <div className="d-flex flex-wrap gap-1">
                        <ImportMappingButton
                          label="CSVをインポート"
                          fields={importFields}
                          onImport={handleImport}
                          storageKey={`product-unit-cost-import-mapping-${tenant.id}`}
                        />
                        <ExportButton
                          label="サンプルCSV"
                          fileName="原価設定サンプル.csv"
                          rows={sampleRowsForImport}
                        />
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
          <div>
            <ProductKpiTable
              tenant={tenant}
              dateRange={[startDate, endDate]}
              sellers={sellers}
              sellersById={sellersById}
              shopsById={shopsById}
              productTags={productTags}
              productTagsById={productTagsById}
              visibleColumns={visibleColumns}
              isExcludeTax={isExcludeTax}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductKpiTable({ tenant, dateRange, sellers, sellersById, shopsById, productTags, productTagsById, visibleColumns, isExcludeTax }) {
  const [startDate, endDate] = dateRange;
  const queryParams = useQueryParams();
  const history = useHistory();
  const location = useLocation();
  const sortBy = queryParams.sortBy || 'sales';
  const sortOrder = queryParams.sortOrder || 'desc';
  const page = parseInt(queryParams.page) || 1;
  const limit = 50;

  const purchases = useCollectionSubscription(
    query(collection(tenant.ref, 'purchases'), dbOrderBy('inboundDate', 'desc'))
  );

  const purchaseItemsGroupedByProductId = useMemo(
    _ => groupBy(
      purchases.flatMap(_ => _.items.map(i => ({ ...i, purchase: _ }))),
      'productId'
    ),
    [purchases]
  );

  const handleSort = (field) => {
    const newSortOrder = (sortBy === field && sortOrder === 'desc') ? 'asc' : 'desc';
    const newParams = { ...queryParams, sortBy: field, sortOrder: newSortOrder, page: 1 };
    history.push(`${location.pathname}?${qs.stringify(newParams)}`);
  };

  const handlePageChange = (newPage) => {
    const newParams = { ...queryParams, page: newPage };
    history.push(`${location.pathname}?${qs.stringify(newParams)}`);
  };

  // Cloud Functionsからデータ取得
  const ownerIds = !isEmpty(queryParams.ownerIds) ? (Array.isArray(queryParams.ownerIds) ? queryParams.ownerIds : [queryParams.ownerIds]) : [];
  const selectedSellerIds = ownerIds.filter(_ => _.startsWith('seller:')).map(_ => _.slice(7));
  const selectedShopIds = ownerIds.filter(_ => _.startsWith('shop:')).map(_ => _.slice(5));

  const { loading, value: result, error, retry: refetchData } = useAsyncRetry(async () => {
    const response = await getProductKpis({
      tenantId: tenant.id,
      startDate: formatDate(startDate, 'yyyy-MM-dd'),
      endDate: formatDate(endDate, 'yyyy-MM-dd'),
      sortBy,
      sortOrder,
      page,
      limit,
      keyword: !isEmpty(queryParams.keyword) ? queryParams.keyword : undefined,
      onlyNoPurchases: queryParams.onlyNoPurchases === '1',
      tagIds: !isEmpty(queryParams.tagIds) ? (Array.isArray(queryParams.tagIds) ? queryParams.tagIds : [queryParams.tagIds]) : undefined,
      isExcludeTax,
      sellerIds: selectedSellerIds.length > 0 ? selectedSellerIds : undefined,
      shopIds: selectedShopIds.length > 0 ? selectedShopIds : undefined,
    });
    return response.data;
  }, [tenant.id, startDate, endDate, sortBy, sortOrder, page, queryParams.keyword, queryParams.onlyNoPurchases, queryParams.tagIds, queryParams.ownerIds, isExcludeTax]);

  const { products = [], pagination = {}, incompleteParents = [] } = result || {};

  if (error) {
    return <div className="text-center p-4 text-danger">エラーが発生しました: {error.message}</div>;
  }


  return (
    <div>
      {
        !isEmpty(incompleteParents) && (
          <div className="alert alert-warning">
            <div className="fw-bold mb-1">Amazon側の出品エラーにより表示できない商品があります</div>
            <div className="small mb-2">
              以下の親SKUにエラーがあるため、Amazonが配下の子商品の処理を停止しています。
              Amazonセラーセントラルで親SKUのエラーを修正すると、次回の商品取得で表示されます。
            </div>
            <ul className="mb-0 small">
              {incompleteParents.map(_ => (
                <li key={`${_.sellerId}__${_.parentSku}`}>
                  親SKU: <span className="font-monospace">{_.parentSku}</span>
                  {' / 対象商品: '}
                  <span className="font-monospace">{_.childAsins.join(', ') || _.childSkus.join(', ')}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      }
      <div className="position-relative">
        <div className="position-relative overflow-auto" style={{ maxHeight: '90vh' }}>
          <table className="table sticky-table table-bordered position-relative table-hover rounded table-sm" style={{ zIndex: 0 }} border="1">
            <thead className="thead-light text-center align-top">
              <tr>
                {
                  [
                    <th>
                    </th>,
                    <th>
                      ASIN / SKU
                    </th>,
                    <th>
                      ショップ
                    </th>,
                    <th>
                      画像
                    </th>,
                    <th>
                      商品名
                    </th>,
                  ].map((el, i) => {
                    return {
                      ...el,
                      key: i,
                      props: {
                        ...el.props,
                        className: 'sticky',
                        style: {
                          ...el.props.style,
                          minWidth: leftColumnWidths[i],
                          left: sum(leftColumnWidths.slice(0, i)) + 1,
                        },
                      },
                    };
                  })
                }
                {columnDefinitions.filter(_ => visibleColumns.includes(_.key)).map(col => (
                  col.sortable ? (
                    <SortableTh key={col.key} field={col.key} currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={handleSort} style={{ minWidth: col.minWidth }}>
                      {col.label}<MallBadge feeKey={col.key} />{col.help && <ColumnHelp columnKey={col.key} help={col.help} />}
                    </SortableTh>
                  ) : (
                    <th key={col.key} style={{ minWidth: col.minWidth }}>{col.label}<MallBadge feeKey={col.key} />{col.help && <ColumnHelp columnKey={col.key} help={col.help} />}</th>
                  )
                ))}
              </tr>
            </thead>
            {
              products.map(product => (
                <ProductRowGroup
                  key={product.productId}
                  product={product}
                  tenant={tenant}
                  sellersById={sellersById}
                  shopsById={shopsById}
                  dateRange={dateRange}
                  purchaseItemsGroupedByProductId={purchaseItemsGroupedByProductId}
                  productTags={productTags}
                  productTagsById={productTagsById}
                  onUpdate={refetchData}
                  visibleColumns={visibleColumns}
                  isExcludeTax={isExcludeTax}
                />
              ))
            }
          </table>
        </div>
        <OverlayLoading isOpen={loading} justify="start" />
      </div>
      
      {pagination.totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={pagination.totalPages}
          hasPrevPage={pagination.hasPrevPage}
          hasNextPage={pagination.hasNextPage}
          onChange={handlePageChange}
        />
      )}
    </div>
  );
}

function ProductRowGroup({ product, tenant, sellersById, shopsById, dateRange, purchaseItemsGroupedByProductId, productTags, productTagsById, onUpdate, visibleColumns, isExcludeTax }) {
  const [isOpen, toggle] = useToggle(false);
  const queryParams = useQueryParams();
  const sortBy = queryParams.sortBy || 'sales';
  const sortOrder = queryParams.sortOrder || 'desc';

  // 商品詳細をsubscribeで取得（タグ等のリアルタイム反映のため）
  const productDetail = useDocumentSubscription(doc(tenant.ref, 'products', product.id), [product.id]);

  const mallDef = getMallDefinition(product.mall);
  const parentKey = getParentKey(product);

  // 子商品データを取得（開いた時のみ）。親キーと子判定はモールごとに異なる
  const childProducts = useCollectionSubscription(
    isOpen && parentKey && query(
      collection(tenant.ref, 'products'),
      where(mallDef.parentField, '==', parentKey),
      where(mallDef.isChildField, '==', mallDef.isChildValue),
    ),
    [isOpen, parentKey, product.mall]
  );

  const hasChildProducts = (product.amazonAttributes?.familySkus?.length > 1)
    || (product.rakutenAttributes?.familySkus?.length > 0)
    || (product.shopifyAttributes?.familySkus?.length > 0)
    || (product.yahooAttributes?.familySkus?.length > 0);
  const amazonImageUrl = productDetail?.amazonAttributes?.summaries?.[0]?.mainImage?.link;
  const rakutenImageLocation = productDetail?.rakutenAttributes?.images?.[0]?.location;
  const rakutenShopUrl = shopsById[product.shopId]?.rakutenShopUrl;
  const shopifyImageUrl = productDetail?.shopifyAttributes?.imageUrl;
  const yahooImageUrl = productDetail?.yahooAttributes?.imageUrl;
  const imageUrl = amazonImageUrl
    || shopifyImageUrl
    || yahooImageUrl
    || (rakutenImageLocation && rakutenShopUrl ? `https://image.rakuten.co.jp/${rakutenShopUrl}/cabinet${rakutenImageLocation}` : null);
  const asin = product.amazonAttributes?.asin;
  const ownerName = mallDef.ownerIdField === 'shopId'
    ? shopsById[product.shopId]?.name
    : sellersById[product.sellerId]?.name;

  return (
    <tbody className="border border-3">
      {/* 親商品の行 */}
      <tr className="table-light">
        {
          [
            <td>
              {hasChildProducts && (
                <Button 
                  color="link" 
                  outline
                  size="sm" 
                  className="p-0" 
                  onClick={() => toggle(!isOpen)}
                >
                  <span className={classnames('fas', { 'fa-plus': !isOpen, 'fa-minus': isOpen })} />
                </Button>
              )}
            </td>,
            <td className="text-break">
              {asin && (
                <div>
                  <a href={`https://www.amazon.co.jp/dp/${asin}`} target="_blank" rel="noopener noreferrer">
                    {asin}
                  </a>
                </div>
              )}
              <div>{product.sku}</div>
              <div className="text-nowrap d-flex justify-content-end gap-1">
                <EditButton label={false} size="sm" itemRef={productDetail?.ref} FormModal={ProductFormModal} formProps={{ title: '商品', fields: productFields(productTags, { hasChildren: hasChildProducts }), }} onFinish={onUpdate} />
                <DeleteButton label={false} size="sm" itemRef={productDetail?.ref} />
                <ModalButton
                  size="sm"
                  color="info"
                  title="日次KPI"
                  content={_ => <DailyProductKpiModal product={product} tenant={tenant} dateRange={dateRange} visibleColumns={visibleColumns} />}
                  modalProps={{ style: { minWidth: '70vw', } }}
                >
                  <span className="fas fa-chart-line" />
                </ModalButton>
              </div>
            </td>,
            <td className="text-break">
              <span className="badge bg-secondary me-1">{mallDef.label}</span>
              {ownerName}
            </td>,
            <td>
              {imageUrl && (
                <img src={imageUrl} style={{ maxHeight: 80, maxWidth: 80 }} alt="商品画像" />
              )}
            </td>,
            <td style={{ maxWidth: '200px' }}>
              <div style={{ height: 80, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <div style={{ flex: 1, overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.3 }}>{product.name}</div>
                {(productDetail?.tagIds || []).length > 0 && (
                  <div className="d-flex flex-wrap gap-1 flex-shrink-0" style={{ overflow: 'hidden', maxHeight: 22 }}>
                    {(productDetail?.tagIds || []).map(tagId => productTagsById[tagId] && (
                      <span key={tagId} className="badge bg-secondary" style={{ fontSize: '0.75em' }}>{productTagsById[tagId].name}</span>
                    ))}
                  </div>
                )}
                <Button id={`product-name-popover-${toSafeDomId(product.id)}`} type="button" color="light" size="sm" className="p-0 px-1 position-absolute" style={{ bottom: 0, right: 0, lineHeight: 1, fontSize: '0.7rem' }}>
                  <span className="fas fa-ellipsis" />
                </Button>
                <UncontrolledPopover target={`product-name-popover-${toSafeDomId(product.id)}`} trigger="legacy" placement="top">
                  <PopoverBody style={{ wordBreak: 'break-word', maxWidth: 320 }}>{product.name}</PopoverBody>
                </UncontrolledPopover>
              </div>
            </td>,
          ].map((el, i) => {
            return {
              ...el,
              key: i,
              props: {
                ...el.props,
                className: classnames(el.props.className, 'sticky'),
                style: {
                  ...el.props.style,
                  minWidth: leftColumnWidths[i],
                  left: sum(leftColumnWidths.slice(0, i)) + 1,
                },
              },
            };
          })
        }
        {renderKpiCells(
          product.kpis,
          product.unitCost,
          product.unitShippingCost,
          visibleColumns
        )}
      </tr>

      {/* 子商品の行 */}
      {isOpen && childProducts && (
        <ChildProductsList
          childProducts={childProducts}
          tenant={tenant}
          sellersById={sellersById}
          shopsById={shopsById}
          dateRange={dateRange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          purchaseItemsGroupedByProductId={purchaseItemsGroupedByProductId}
          productTags={productTags}
          productTagsById={productTagsById}
          onUpdate={onUpdate}
          visibleColumns={visibleColumns}
          isExcludeTax={isExcludeTax}
        />
      )}
    </tbody>
  );
}

function ChildProductsList({ childProducts, tenant, sellersById, shopsById, dateRange, sortBy, sortOrder, purchaseItemsGroupedByProductId, productTags, productTagsById, onUpdate, visibleColumns, isExcludeTax }) {
  const [startDate, endDate] = dateRange;
  const days = differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 1;
  
  const childMallDef = getMallDefinition(childProducts[0]?.mall);
  const salesCollectionName = childMallDef.productSalesCollection;
  const salesData = useCollectionsFetch(chunk(childProducts.map(p => p.id), 10).map(ids =>
    query(collection(tenant.ref, salesCollectionName), where('productId', 'in', ids), where('date', '>=', formatDate(startDate, 'yyyy-MM-dd')), where('date', '<=', formatDate(endDate, 'yyyy-MM-dd')))
  ), [childProducts, startDate, endDate]);

  // 楽天は productId+date の複合インデックスが未整備のため、date の where を外して
  // productId in のみで取得し、日付は下のクライアント側フィルタで絞る（複合インデックス不要）。
  // 広告未対応モール(Shopify)は null。その場合はクエリを投げない
  const adCollectionName = childMallDef.productAdKpisCollection;
  const startDateStr = formatDate(startDate, 'yyyy-MM-dd');
  const endDateStr = formatDate(endDate, 'yyyy-MM-dd');
  const adDataRaw = useCollectionsFetch(adCollectionName == null ? [] : chunk(childProducts.map(p => p.id), 10).map(ids =>
    childMallDef.hasAdKpisDateIndex
      ? query(collection(tenant.ref, adCollectionName), where('productId', 'in', ids), where('date', '>=', startDateStr), where('date', '<=', endDateStr))
      : query(collection(tenant.ref, adCollectionName), where('productId', 'in', ids))
  ), [childProducts, startDate, endDate]);
  const adData = (adDataRaw || []).filter(_ => _.date >= startDateStr && _.date <= endDateStr);

  const handleTax = isExcludeTax ? excludeTax : _ => _;
  const handleTaxExcludedValue = isExcludeTax ? _ => _ : includeTax;
  const sortedChildProducts = useMemo(() =>
    orderBy(
      childProducts.map(childProduct => {
        const productSalesData = salesData?.filter(_ => _.productId === childProduct.id) || [];
        const productAdData = adData?.filter(_ => _.productId === childProduct.id) || [];

        const sales = handleTax(sumBy(productSalesData, _ => _.values?.sales || 0));
        const refund = handleTax(sumBy(productSalesData, _ => _.values?.refund || 0));
        const quantity = sumBy(productSalesData, _ => _.values?.quantity || 0);
        const refundQuantity = sumBy(productSalesData, _ => _.values?.refundQuantity || 0);
        const salesFees = handleTax(sumBy(productSalesData, _ => _.values?.commission || 0));
        const systemUsageFee = handleTax(sumBy(productSalesData, _ => _.values?.systemUsageFee || 0));
        const safetyFee = handleTax(sumBy(productSalesData, _ => _.values?.safetyFee || 0));
        const paymentFee = handleTax(sumBy(productSalesData, _ => _.values?.paymentFee || 0));
        const fbaFees = handleTax(sumBy(productSalesData, _ => _.values?.fbaFee || 0));
        const pointsBaseCost = handleTax(sumBy(productSalesData, _ => _.values?.pointsBaseCost || 0));
        const pointsCost = handleTax(sumBy(productSalesData, _ => _.values?.pointsCost || 0));
        const promotionCost = handleTax(sumBy(productSalesData, _ => _.values?.promotionCost || 0));
        const vineQuantity = sumBy(productSalesData, _ => _.values?.vineQuantity || 0);
        const vineSales = handleTax(sumBy(productSalesData, _ => _.values?.vineSales || 0));
        const adCost = handleTaxExcludedValue(sumBy(productAdData, _ => _.kpis?.cost || 0));
        // 広告売上のフィールド名はモールで異なる（Amazon=sales1d / 楽天=sales）
        const adSales = handleTax(sumBy(productAdData, _ => _.kpis?.[childMallDef.adSalesField] || 0));

        // 原価計算: 売上docの日付ごとに有効原価(unitCostHistory)を引いて正味数量(返品・Vine控除後)を掛ける
        const costTaxRate = taxRates[childProduct.costTaxRate]?.value;
        const cost = sumBy(productSalesData, doc => {
          const raw = resolveUnitCostAt(childProduct, doc.date);
          const dailyUnitCost = isExcludeTax ? excludeTax(raw, costTaxRate) : raw;
          return dailyUnitCost * netQuantityOf(doc.values);
        });
        const netQuantity = sumBy(productSalesData, _ => netQuantityOf(_.values));
        // 表示用は期間の実効平均単価（販売0のときは原始原価にフォールバック）
        const unitCost = netQuantity > 0
          ? Math.round(cost / netQuantity)
          : (isExcludeTax ? excludeTax(childProduct.unitCost || 0, costTaxRate) : (childProduct.unitCost || 0));

        // 配送料計算
        const unitShippingCost = isExcludeTax ? excludeTax(childProduct.unitShippingCost || 0) : (childProduct.unitShippingCost || 0);
        const fbaShippingCost = Math.abs(fbaFees);
        const shippingCost = unitShippingCost * sumBy(productSalesData, _ => shippingQuantityOf(_.values));

        const netSales = sales + refund - vineSales;
        // ポイント原資(絶対負担1%)は原価2扱いなので純粗利益から控除。pointsBaseCostは負値
        const grossProfit = netSales - cost + salesFees + systemUsageFee + safetyFee + paymentFee - fbaShippingCost - shippingCost + pointsBaseCost;

        const organicSales = netSales - adSales;

        return {
          ...childProduct,
          unitCost,
          unitShippingCost,
          kpis: {
            grossSales: sales,
            sales: sales + refund - vineSales,
            refund: Math.abs(refund),
            grossQuantity: quantity,
            quantity: quantity - refundQuantity - vineQuantity,
            refundQuantity,
            vineQuantity,
            vineSales,
            cost,
            salesFees: Math.abs(salesFees),
            systemUsageFee: Math.abs(systemUsageFee),
            safetyFee: Math.abs(safetyFee),
            paymentFee: Math.abs(paymentFee),
            pointsBaseCost: Math.abs(pointsBaseCost),
            fbaShippingCost,
            shippingCost,
            grossProfit,
            grossProfitRate: netSales > 0 ? grossProfit / netSales : 0,
            adCost,
            adSales,
            adSalesRatio: netSales > 0 ? adSales / netSales : 0,
            organicSales,
            organicSalesRatio: netSales > 0 ? organicSales / netSales : 0,
            pointsCost,
            promotionCost,
            contributionMargin: grossProfit - adCost + pointsCost + promotionCost,
            contributionMarginRate: netSales > 0 ? (grossProfit - adCost + pointsCost + promotionCost) / netSales : 0,
            acos: adSales > 0 ? adCost / adSales : 0,
            acots: netSales > 0 ? adCost / netSales : 0,
            fbaQuantity: childProduct.fbaQuantity || 0,
            fbaInventoryAmount: (childProduct.fbaQuantity || 0) * unitCost,
            dailyAverageQuantity: days > 0 ? (quantity - refundQuantity - vineQuantity) / days : 0,
            daysUntilStockout: (days > 0 && (quantity - refundQuantity - vineQuantity) > 0)
              ? (childProduct.fbaQuantity || 0) / ((quantity - refundQuantity - vineQuantity) / days)
              : null,
          }
        };
      }),
      [`kpis.${sortBy}`],
      [sortOrder]
    ),
    [childProducts, salesData, adData, sortBy, sortOrder, purchaseItemsGroupedByProductId, isExcludeTax]
  );

  return (
    <>
      {sortedChildProducts.map(childProduct => (
        <ChildProductRow key={childProduct.id} childProduct={childProduct} sellersById={sellersById} shopsById={shopsById} tenant={tenant} dateRange={dateRange} productTags={productTags} productTagsById={productTagsById} onUpdate={onUpdate} visibleColumns={visibleColumns} />
      ))}
    </>
  );
}

function ChildProductRow({ childProduct, sellersById, shopsById, tenant, dateRange, productTags, productTagsById, onUpdate, visibleColumns }) {
  const kpis = childProduct.kpis;
  const childMallDef = getMallDefinition(childProduct.mall);

  const amazonImageUrl = childProduct.amazonAttributes?.summaries?.[0]?.mainImage?.link;
  const rakutenImageLocation = childProduct.rakutenAttributes?.images?.[0]?.location;
  const rakutenShopUrl = shopsById?.[childProduct.shopId]?.rakutenShopUrl;
  const shopifyChildImageUrl = childProduct.shopifyAttributes?.imageUrl;
  const yahooChildImageUrl = childProduct.yahooAttributes?.imageUrl;
  const childImageUrl = amazonImageUrl
    || shopifyChildImageUrl
    || yahooChildImageUrl
    || (rakutenImageLocation && rakutenShopUrl ? `https://image.rakuten.co.jp/${rakutenShopUrl}/cabinet${rakutenImageLocation}` : null);
  const childAsin = childProduct.amazonAttributes?.asin;

  return (
    <tr className="child-row bg-white">
      {
        [
          <td></td>,
          <td className="text-break">
            <div>
              <a href={`https://www.amazon.co.jp/dp/${childAsin}`} target="_blank" rel="noopener noreferrer">
                {childAsin}
              </a>
            </div>
            <div>{childProduct.sku}</div>
            <div className="text-nowrap d-flex justify-content-end gap-1">
              <EditButton label={false} size="sm" itemRef={childProduct.ref} FormModal={ProductFormModal} formProps={{ title: '商品', fields: productFields(productTags), }} onFinish={onUpdate} />
              <DeleteButton label={false} size="sm" itemRef={childProduct.ref} />
              <ModalButton
                size="sm"
                color="info"
                title="日次KPI"
                content={_ => <DailyProductKpiModal product={childProduct} tenant={tenant} dateRange={dateRange} visibleColumns={visibleColumns} />}
                modalProps={{ style: { minWidth: '70vw', } }}
              >
                <span className="fas fa-chart-line" />
              </ModalButton>
            </div>
          </td>,
          <td className="text-break">
            {childMallDef.ownerIdField === 'shopId'
              ? shopsById?.[childProduct.shopId]?.name
              : sellersById[childProduct.sellerId]?.name}
          </td>,
          <td>
            {childImageUrl && (
              <img src={childImageUrl} style={{ maxHeight: 80, maxWidth: 80 }} alt="商品画像" />
            )}
          </td>,
          <td style={{ maxWidth: '200px' }}>
            <div style={{ height: 80, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
              <div style={{ flex: 1, overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.3 }}>{childProduct.name}</div>
              {(childProduct.tagIds || []).length > 0 && (
                <div className="d-flex flex-wrap gap-1 flex-shrink-0" style={{ overflow: 'hidden', maxHeight: 22 }}>
                  {(childProduct.tagIds || []).map(tagId => productTagsById[tagId] && (
                    <span key={tagId} className="badge bg-secondary" style={{ fontSize: '0.75em' }}>{productTagsById[tagId].name}</span>
                  ))}
                </div>
              )}
              <Button id={`product-name-popover-${toSafeDomId(childProduct.id)}`} type="button" color="light" size="sm" className="p-0 px-1 position-absolute" style={{ bottom: 0, right: 0, lineHeight: 1, fontSize: '0.7rem' }}>
                <span className="fas fa-ellipsis" />
              </Button>
              <UncontrolledPopover target={`product-name-popover-${toSafeDomId(childProduct.id)}`} trigger="legacy" placement="top">
                <PopoverBody style={{ wordBreak: 'break-word', maxWidth: 320 }}>{childProduct.name}</PopoverBody>
              </UncontrolledPopover>
            </div>
          </td>,
        ].map((el, i) => {
          return {
            ...el,
            key: i,
            props: {
              ...el.props,
              className: classnames(el.props.className, 'sticky'),
              style: {
                ...el.props.style,
                minWidth: leftColumnWidths[i],
                left: sum(leftColumnWidths.slice(0, i)) + 1,
              },
            },
          };
        })
      }
      {renderKpiCells(
        kpis,
        childProduct.unitCost,
        childProduct.unitShippingCost,
        visibleColumns
      )}
    </tr>
  );
}
